import { JI_RATIOS, getRaga, noteToFrequency, ragaTargetCents } from '@swarasense/shared';
import type { GamakaProfileEntry, SwaraSymbol } from '@swarasense/shared';
import { ragaSeedFor } from '../api/seed.js';
import type { SessionState } from './store.js';

/** The allowed swaras for a ragam, by name — the ladder's lit rungs and the
 * grader's/coach's "in scale" set. `[]` for an unknown name so callers never
 * have to null-check. */
export function ragaSwarasFor(ragaName: string): readonly SwaraSymbol[] {
  return getRaga(ragaName)?.swaras ?? [];
}

/**
 * The Sa (tonic) frequency grading should be measured against: the "detect
 * from drone" locked tonic when Setup's calibration check ran and passed,
 * otherwise the theoretical frequency for the selected pitch class/octave.
 *
 * Centralized here so every grading call site reads the SAME Sa — before
 * this helper existed, `live.ts` and `summary.ts` each independently
 * recomputed Sa from `pitchClass`/`octave`, silently ignoring a successful
 * calibration lock even though setup.ts displayed it as "Locked: X Hz".
 */
export function saHzFor(
  state: Pick<SessionState, 'calibration' | 'pitchClass' | 'octave'>,
): number {
  return state.calibration?.locked
    ? state.calibration.saHz
    : noteToFrequency(`${state.pitchClass}${state.octave}`);
}

export interface Scoring {
  targetCents: Record<string, number>;
  gamakaProfile: Record<string, GamakaProfileEntry>;
}

/**
 * Target cents + gamaka profile for a ragam. Prefers the seed record (per-raga
 * swara_variants + intonation_overrides, via `ragaTargetCents`); when the seed
 * doesn't have this raga, falls back to plain 12-symbol just intonation over
 * `ragaSwarasFor()` so grading never breaks. Shared by `live.ts` (live pitch
 * grading + `analyzeSession`) and `summary.ts` (redrawing the review contour)
 * so both use the same target cents for a given ragam, as before Task 3.
 */
export function resolveScoring(ragaName: string): Scoring {
  const record = ragaSeedFor(ragaName);
  if (record) {
    return { targetCents: ragaTargetCents(record), gamakaProfile: record.gamaka_profile ?? {} };
  }
  const targetCents: Record<string, number> = {};
  for (const sym of ragaSwarasFor(ragaName)) targetCents[sym] = 1200 * Math.log2(JI_RATIOS[sym]);
  return { targetCents, gamakaProfile: {} };
}
